import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getPortal, portalDrill, sendMessage, askForSomething, subscribePark } from '../../api/portal';
import { useView } from '../../contexts/ViewContext';
import PanelErrorBoundary from '../shared/PanelErrorBoundary';
import GenericCard from '../shared/GenericCard';
import DrillOver from '../shared/DrillOver';
import { Skeleton, SkeletonText, SkeletonChart } from '../shared/Skeleton';
import { ErrorState, EmptyState } from '../shared/StateMessage';
import VerdictPanel from './VerdictPanel';
import WaitingOnYouPanel from './WaitingOnYouPanel';
import TrackerPanel from './TrackerPanel';
import ItemDetail from './ItemDetail';
import ListDetail from './ListDetail';
import ConversationPanel from './ConversationPanel';
import MessageTeamButton from './MessageTeamButton';
import DoneRecentlyPanel from './DoneRecentlyPanel';
import TeamPanel from './TeamPanel';
import HistoryPanel from './HistoryPanel';
import ParkFilter from './ParkFilter';
import AskSheet from './AskSheet';
import usePortalDeepLink from './usePortalDeepLink';
import AdminHeader from '../AdminHeader';
import ClientHeader from '../ClientHeader';
import NavSidebar from '../NavSidebar';
import { usePageMeta } from '../DashboardShell';

/**
 * Portal "MY WORK" — the page shell (Epic 3).
 *
 * Owns the ONE `getPortal(parkId)` call (load + park-filter change), places the
 * six panels by `panel` key into the Part B regions (never sorts/drops — §5.7),
 * and owns all page state: the park filter, the `DrillOver` stack, and (later
 * epics) the open sheets. Region `PanelErrorBoundary`s keep one panel's failure
 * from blanking the page.
 *
 * States (Part G): loading → skeletons; error → ErrorState banner verbatim +
 * Retry; no_data → EmptyState; not-authorised (deep link) → ErrorState verbatim;
 * can_write === false → note "You're seeing what the client sees."
 *
 * `date_range` is always "live" — the global date picker is hidden on this
 * route (header/datePicker.js `NO_GLOBAL_DATE_RANGE_ROUTES`).
 *
 * The real panel components (VerdictPanel, WaitingOnYouPanel, TrackerPanel, …)
 * land in Epics 4–9. This shell renders lightweight stand-ins for the panels it
 * does not own yet so placement/state/boundary wiring is testable now; each
 * stand-in is swapped for its real component in its epic.
 */

const PANEL_ORDER = [
  'waiting_on_you', 'tracker', 'conversation',
  'done_recently', 'team', 'history',
];
const byKey = (panels, key) =>
  Array.isArray(panels) ? panels.find((p) => p.panel === key) : null;
const unknownPanels = (panels) =>
  Array.isArray(panels) ? panels.filter((p) => !PANEL_ORDER.includes(p.panel)) : [];

// ── Park filter URL persistence (Epic 9 · S9.2, spec Part E.8) ─────────────────
// The app-level property selector (header) owns the baseline park choice; the
// in-page ParkFilter writes a page-level override to `?park=`. `?park=all` →
// null (the all-parks operator view); `?park=<id>` → that park; absent →
// `undefined` (no override — fall back to the app-level selector). The
// dismissible "Showing {park} ✕" indicator clears the override and returns to
// the app-level client.
const PARK_ALL = 'all';
const readParkParam = (searchParams) => {
  if (!searchParams) return undefined;
  const raw = searchParams.get('park');
  if (raw === null) return undefined;       // no ?park= → use app-level baseline
  if (raw === PARK_ALL) return null;          // ?park=all → all-parks operator view
  return raw || undefined;                    // ?park=<id> → that park
};

// ── Panel stand-ins (swapped for real components in Epics 4–9) ────────────────
// VerdictPanel (Epic 4), WaitingOnYouPanel (Epic 4), ConversationPanel (Epic 7),
// DoneRecentlyPanel/TeamPanel/HistoryPanel (Epic 8) are now real components —
// their stand-ins were removed. No stand-ins remain.


// ── Loading shell ─────────────────────────────────────────────────────────────
const PortalSkeleton = () => (
  <div role="status" aria-label="Loading MY WORK" aria-live="polite" className="space-y-4">
    <Skeleton className="h-16 w-full rounded-xl" />
    <SkeletonText lines={3} lineClass="h-4" gapClass="space-y-2" />
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <div className="lg:col-span-8 space-y-4">
        <Skeleton className="h-24 w-full rounded-xl" />
        <SkeletonText lines={3} lineClass="h-10" />
      </div>
      <div className="lg:col-span-4">
        <SkeletonChart height={240} className="rounded-xl" />
      </div>
    </div>
    <span className="sr-only">Loading…</span>
  </div>
);

// Reads the selected park from ViewContext (the header's property selector).
// This is the BASELINE park choice — the app-level selector still owns it
// (Part E.8 / Epic 9 goal). The in-page ParkFilter writes a `?park=` override
// on top of this; `parkId` below resolves override ?? baseline.
const usePark = () => {
  const view = useView();
  return {
    parkId: view?.selectedProject?.client_id ?? view?.selectedProject?.id ?? null,
    selectProject: view?.selectProject ?? (() => {}),
    isClient: !!view?.isClient,
  };
};

// ── Page ──────────────────────────────────────────────────────────────────────
const PortalPage = () => {
  // Persistent chrome (header + sidebar) is provided by <DashboardLayout/> in
  // App.js. Declare which nav items to highlight, then render only the content.
  // Called unconditionally at the TOP of the component (before any early return
  // below) so React's hook order stays stable across renders.
  usePageMeta({ headerActive: 'MY WORK', sidebarActive: 'my-work' });
  const { parkId: appLevelParkId, selectProject, isClient } = usePark();
  const [searchParams, setSearchParams] = useSearchParams();
  // Dashboard chrome: Portal rides inside the same shell as every other page
  // (header + left sidebar), so it inherits the app-wide nav and property
  // selector. The client-facing login (role 3 / org_viewer / org_scoped) gets
  // ClientHeader; staff get AdminHeader. NavSidebar highlights the MY WORK row.
  const _rawUsername = (typeof localStorage !== 'undefined' && localStorage.getItem('username') && localStorage.getItem('username') !== 'undefined') ? localStorage.getItem('username') : null;
  const username = _rawUsername || (isClient ? 'client_user' : 'username');
  const initials = username.slice(0, 2).toUpperCase();
  // Page-level park override from `?park=` (Epic 9 · S9.2). `undefined` → no
  // override (use the app-level baseline); `null` → all-parks; `<id>` → park.
  const filterPark = readParkParam(searchParams);
  const parkId = filterPark !== undefined ? filterPark : appLevelParkId;

  // Deep link (Epic 11 · S11.2, Part E.10): `?portal=<parkId>&open=<what>` from
  // email links. Fires ONCE on mount; the hook clears the params from the URL.
  const { deepParkId, open: deepOpen } = usePortalDeepLink();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
    const [drillStack, setDrillStack] = useState([]);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  // Deep-link `open=<what>` target (Epic 11 · S11.2): `message:<id>` → the
  // ConversationPanel scrolls to it (and loads older if needed); `done` → scroll
  // to the Done recently panel. `item:<id>` opens a DrillOver directly.
  const [focusMessageId, setFocusMessageId] = useState(null);
  const doneRef = useRef(null);
  // Guards the deep-link `open` target so it fires once (Part E.10 — the link
  // fires once, then clears itself from the URL).
  const openHandled = useRef(false);
  const reqId = useRef(0);
  // Live-update debounce timers (Epic 11 · S11.1) — ref so successive events
  // reset the same timer instead of stacking refetches.
  const messageTimer = useRef(null);
  const workTimer = useRef(null);

  const load = useCallback(async (id) => {
    const myReq = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await getPortal(id);
      if (myReq !== reqId.current) return;
      setData(res.data);
    } catch (e) {
      if (myReq !== reqId.current) return;
      setError(e?.message || "Couldn't load MY WORK.");
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  }, []);

  // One getPortal on load + refetch on park-filter change ONLY.
  useEffect(() => {
    load(parkId);
  }, [parkId, load]);

  // Live updates (Epic 11 · S11.1, Part A.8): subscribePark for the park on
  // screen; one subscription per park in the all-parks view. `message` →
  // refetch (debounce 1s); `work` → refetch (debounce 2s). Unsubscribe on park
  // change + unmount. Subscription failure is non-fatal — the page refreshes on
  // the next load (Part A.8). The refetch re-renders ConversationPanel, which
  // calls markRead after render (Epic 7) — so the badge clears when the convo is
  // open+visible.
  useEffect(() => {
    // The set of parks to watch: the on-screen park (single) or every park
    // (all-parks operator view). null parkId with a single park → no broadcast
    // to subscribe to (nothing on screen to reconcile).
    const watch = (parkId === null)
      ? (Array.isArray(data?.parks) ? data.parks.map((p) => p?.client_id ?? p?.id).filter(Boolean) : [])
      : (parkId ? [parkId] : []);
    if (watch.length === 0) return;

    let unsubscribers = [];
    let cancelled = false;
    // Debounced refetch — the timers live on refs (declared at the component
    // top level) so successive events reset the same timer instead of stacking.
    const scheduleRefetch = (ms) => () => { load(parkId); };

    (async () => {
      for (const pid of watch) {
        try {
          const unsub = await subscribePark(pid, (event) => {
            if (event === 'message') {
              if (messageTimer.current) clearTimeout(messageTimer.current);
              messageTimer.current = setTimeout(scheduleRefetch(1000), 1000);
            } else if (event === 'work') {
              if (workTimer.current) clearTimeout(workTimer.current);
              workTimer.current = setTimeout(scheduleRefetch(2000), 2000);
            }
          });
          if (cancelled) { try { unsub?.(); } catch {} }
          else unsubscribers.push(unsub);
        } catch {
          /* subscription failure is non-fatal (Part A.8) */
        }
      }
    })();

    return () => {
      cancelled = true;
      if (messageTimer.current) clearTimeout(messageTimer.current);
      if (workTimer.current) clearTimeout(workTimer.current);
      unsubscribers.forEach((u) => { try { u?.(); } catch {} });
    };
  }, [parkId, data?.parks, load]);

  // Deep link → select the park + handle `open` (Epic 11 · S11.2, Part E.10).
  // Fires once on mount (deepParkId is read once by the hook). Selecting the
  // app-level park drives the load via the parkId effect; for the all-parks
  // operator view deepParkId is null (just open on whatever the app-level park
  // is). `open=notifications` is NOT handled here (separate build) — it's left
  // for App.js to route.
  useEffect(() => {
    if (deepParkId === null || deepParkId === undefined) return;
    // Select that park at the app level so the header's property selector
    // agrees, and set the page-level override so the page shows it.
    selectProject?.(deepParkId);
    handleSelectPark(deepParkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepParkId]);

  // ParkFilter selection (Epic 9 · S9.1) — writes the page-level override to
  // `?park=`. The app-level selector is NOT mutated (it stays the baseline);
  // the `parkId` derived above re-resolves and the load effect refetches.
  // `null` (All parks) → `?park=all`; `<id>` → `?park=<id>`. `replace` keeps the
  // filter from cluttering browser history.
  const handleSelectPark = useCallback((parkIdOrNull) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (parkIdOrNull === null || parkIdOrNull === undefined) next.set('park', PARK_ALL);
      else next.set('park', String(parkIdOrNull));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Dismiss the page-level override → return to the app-level client (the
  // header's property selector). Removes `?park=` so a refresh keeps the
  // app-level choice too.
  const dismissFilter = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('park');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const popDrill = useCallback(() => setDrillStack((s) => s.slice(0, -1)), []);
  const closeDrill = useCallback(() => setDrillStack([]), []);

  // Each frame gets a unique key so the async fetch can update the right frame
  // without depending on its stack index (which would be stale inside the
  // closure that pushed it).
  const drillKey = useRef(0);
  const updateFrameByKey = useCallback((key, patch) => {
    setDrillStack((s) => s.map((f) => (f.key === key ? { ...f, ...patch } : f)));
  }, []);

  // openDrill — push a frame and fetch its body (`fn_portal_drill`). The frame
  // carries everything the body needs: kind, id, scope ({ park_id } + extras),
  // label (the DrillOver header), limit, focusComposer, and the resolved
  // result. The label is refreshed from the payload once it resolves.
  const openDrill = useCallback((kind, id, scope, { label, focusComposer = false, limit = 50 } = {}) => {
    const key = ++drillKey.current;
    const frame = {
      key, kind, id, scope,
      label: label || 'Loading…',
      limit, focusComposer,
      loading: true, result: null, error: null,
    };
    setDrillStack((s) => [...s, frame]);
    (async () => {
      try {
        const res = await portalDrill(kind, id, scope, limit);
        updateFrameByKey(key, {
          loading: false,
          result: res?.data ?? null,
          label: res?.data?.label || frame.label,
        });
      } catch (e) {
        updateFrameByKey(key, { loading: false, error: e?.message || "Couldn't load." });
      }
    })();
  }, [updateFrameByKey]);

  // Re-run the top frame's drill with a scope patch (search query / page
  // offset) — used by ListDetail's search (history) + Previous/Next (all lists).
  const refetchTop = useCallback(async (scopePatch) => {
    if (drillStack.length === 0) return;
    const top = drillStack[drillStack.length - 1];
    const scope = { ...top.scope, ...scopePatch };
    updateFrameByKey(top.key, { scope, loading: true, error: null });
    try {
      const res = await portalDrill(top.kind, top.id, scope, top.limit);
      updateFrameByKey(top.key, { loading: false, result: res?.data ?? null });
    } catch (e) {
      updateFrameByKey(top.key, { loading: false, error: e?.message || "Couldn't load." });
    }
  }, [drillStack, updateFrameByKey]);

  // Any row / chip / avatar / "Show all" / "Reply" opens the one DrillOver into
  // an ItemDetail. Reply carries focusComposer so the composer is focused on
  // open (WaitingRow's "Reply" intent, carried on the frame).
  const handleOpenItem = useCallback((item, { focusComposer = false } = {}) => {
    openDrill('item', item?.id, { park_id: item?.park_id ?? parkId }, { label: item?.title, focusComposer });
  }, [openDrill, parkId]);

  // TrackerPanel "Show all {total}" -> a ListDetail drill for the group.
  const handleShowAll = useCallback((group) => {
    openDrill('tracker', null, { park_id: group?.park_id ?? parkId, group: group?.label }, { label: group?.label });
  }, [openDrill, parkId]);

  // Done / History "Show all" + Team avatar -> ListDetail drills (so every
  // drillable thing opens the one DrillOver — Epic 6 acceptance).
  const handleShowAllDone = useCallback(() => {
    openDrill('done_recently', null, { park_id: parkId }, { label: 'Done recently' });
  }, [openDrill, parkId]);

  // Deep-link `open=<what>` handling (Epic 11 · S11.2, Part E.10) — fires once
  // the page data is loaded. `item:<id>` → DrillOver ItemDetail; `message:<id>`
  // → hand the id to ConversationPanel (it scrolls + loads older up to 5×);
  // `done` → scroll to the Done recently panel. `notifications` is NOT handled
  // here (separate build). The open target is consumed once then cleared so a
  // refetch / park change doesn't re-fire it.
  useEffect(() => {
    if (!deepOpen || !data || openHandled.current) return;
    openHandled.current = true;
    if (deepOpen.kind === 'item' && deepOpen.id) {
      openDrill('item', deepOpen.id, { park_id: parkId }, { label: 'Loading…' });
    } else if (deepOpen.kind === 'message' && deepOpen.id) {
      setFocusMessageId(deepOpen.id);
    } else if (deepOpen.kind === 'done') {
      doneRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
    // `notifications` is NOT handled here (separate build — this page doesn't
    // own account-wide notification settings).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepOpen, data, parkId, openDrill]);
  const handleShowAllHistory = useCallback(() => {
    openDrill('history', null, { park_id: parkId }, { label: 'History' });
  }, [openDrill, parkId]);
  const handleOpenMember = useCallback((member) => {
    openDrill('person', member?.id, { park_id: parkId }, { label: member?.label || member?.name });
  }, [openDrill, parkId]);

  // A list row carrying a `drill` opens the next frame — the drill STACKS
  // (§4.10): onRowDrill(row) runs portalDrill(row.drill.value, row.drill.id,
  // {park_id, ...row.drill.scope}) and pushes the item frame on top of the list.
  const handleRowDrill = useCallback((row) => {
    const top = drillStack[drillStack.length - 1];
    const parkIdAnchor = top?.scope?.park_id ?? parkId;
    openDrill(
      row?.drill?.value,
      row?.drill?.id,
      { park_id: parkIdAnchor, ...(row?.drill?.scope || {}) },
      { label: row?.title || row?.primary },
    );
  }, [drillStack, openDrill, parkId]);

  // ListDetail search (history) + paging (all lists) — server-side.
  const handleListSearch = useCallback((q) => refetchTop({ q, offset: 0 }), [refetchTop]);
  const handleListPage = useCallback((offset) => refetchTop({ offset }), [refetchTop]);

  // Reply composer send — optimistic append (Part A.8 reconciles via Realtime).
  // Rolls the optimistic message back if the write fails so the list never
  // shows a message that didn't send; the composer surfaces the error itself.
  const handleSend = useCallback(async ({ parkId: pParkId, itemId, body }) => {
    const top = drillStack[drillStack.length - 1];
    if (top?.kind === 'item' && top.result?.rows?.[0]) {
      const row = top.result.rows[0];
      const prevMessages = Array.isArray(row.messages) ? row.messages : [];
      const appended = { ...row, messages: [...prevMessages, { id: `m-opt-${Date.now()}`, from: 'You', body, when: 'Today', mine: true }] };
      updateFrameByKey(top.key, { result: { ...top.result, rows: [appended, ...top.result.rows.slice(1)] } });
      try {
        await sendMessage({ parkId: pParkId, itemId, body });
      } catch (e) {
        updateFrameByKey(top.key, { result: { ...top.result, rows: [{ ...row, messages: prevMessages }, ...top.result.rows.slice(1)] } });
        throw e;
      }
      return;
    }
    await sendMessage({ parkId: pParkId, itemId, body });
  }, [drillStack, updateFrameByKey]);

  // TrackerPanel empty-state (Verde) Ask CTA + the header "Ask" button → open
  // the AskSheet (Epic 10). The sheet is hidden when `!can_write`; PortalPage
  // gates the trigger buttons on canWrite so the sheet is only reachable by a
  // client who can ask.
  const handleAsk = useCallback(() => setAskOpen(true), []);

  // AskSheet submit (Epic 10 · S10.1) — `askForSomething` then close + refetch.
  // Throws verbatim on a backend raise (Part A.6); the sheet surfaces the
  // message next to the control. The refetch makes the new item appear in the
  // tracker at Asked + the request appear in the conversation (Part E.9).
  const handleAskSubmit = useCallback(async ({ parkId: pParkId, title, detail, needBy, attachmentPaths }) => {
    await askForSomething({
      parkId: pParkId,
      title,
      detail,
      needBy,
      files: Array.isArray(attachmentPaths) && attachmentPaths.length > 0 ? attachmentPaths : null,
    });
    setAskOpen(false);
    load(parkId);
  }, [load, parkId]);

  const renderDrillBody = useCallback((frame) => {
    if (!frame) return null;
    if (frame.error) {
      return <ErrorState variant="inset" message={frame.error} onRetry={() => refetchTop({})} />;
    }
    const multiPark = Array.isArray(data?.parks) && data.parks.length > 1;
    if (frame.kind === 'item') {
      if (frame.loading && !frame.result) {
        return (
          <div className="space-y-3 py-2" role="status" aria-label="Loading item">
            <div className="h-5 w-2/3 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
            <div className="h-4 w-1/3 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
            <div className="h-16 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
            <span className="sr-only">Loading…</span>
          </div>
        );
      }
      return (
        <ItemDetail
          row={frame.result?.rows?.[0]}
          multiPark={multiPark}
          focusComposer={frame.focusComposer}
          onSend={handleSend}
        />
      );
    }
    // List drill (tracker · history · done_recently · person) — rows open items.
    return (
      <ListDetail
        kind={frame.kind}
        result={frame.result}
        loading={frame.loading && !frame.result}
        multiPark={multiPark}
        limit={frame.limit}
        onRowDrill={handleRowDrill}
        onSearch={frame.kind === 'history' ? handleListSearch : undefined}
        onPage={handleListPage}
      />
    );
  }, [data, handleSend, handleRowDrill, handleListSearch, handleListPage, refetchTop]);

  const status = data?.status;
  const canWrite = data?.can_write === true;
  const verdict = data?.verdict;
  const panels = data?.panels;
  const parks = data?.parks;

  if (loading && !data) {
    return (
      <div data-testid="portal-page" className="max-w-7xl mx-auto px-4 py-6">
        <PortalSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="portal-page" className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <ErrorState variant="banner" message={error} onRetry={() => load(parkId)} />
      </div>
    );
  }

  if (status === 'no_data' || (!data && !loading)) {
    return (
      <div data-testid="portal-page" className="max-w-7xl mx-auto px-4 py-6">
        <EmptyState variant="card" title="MY WORK" message="You don't have a park to show here yet." />
      </div>
    );
  }

  const waiting = byKey(panels, 'waiting_on_you');
  const tracker = byKey(panels, 'tracker');
  const conversation = byKey(panels, 'conversation');
  const done = byKey(panels, 'done_recently');
  const team = byKey(panels, 'team');
  const history = byKey(panels, 'history');
  const unknowns = unknownPanels(panels);
  // The page describes the selection (Epic 9 goal). Single-park logins keep the
  // plain title; multi-park logins say which park (or "All parks") is on screen.
  const selectedParkName = Array.isArray(parks) && parkId !== null
    ? (parks.find((p) => (p?.client_id ?? p?.id) === parkId)?.name || null)
    : null;
  const headerTitle = (!parks || parks.length <= 1)
    ? 'MY WORK'
    : (parkId === null ? 'MY WORK · All parks' : (selectedParkName ? `MY WORK · ${selectedParkName}` : 'MY WORK · All parks'));
  // The dismissible "Showing {park} ✕" indicator appears only when a specific
  // park is filtered via `?park=<id>` (an override on top of the app-level
  // client). "All parks" + no-override don't badge — they ARE the baseline.
  const filteredParkName = (filterPark !== undefined && filterPark !== null && Array.isArray(parks))
    ? (parks.find((p) => (p?.client_id ?? p?.id) === filterPark)?.name || null)
    : null;

  return (
    <main className="flex-1 min-w-0">
      <div data-testid="portal-page" className="max-w-7xl mx-auto px-4 py-6 space-y-4">
      <header className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{headerTitle}</h1>
          {canWrite && (
            <button
              type="button"
              onClick={handleAsk}
              data-testid="portal-ask-button"
              className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg text-sm font-medium text-white bg-[#32B1FF] hover:bg-[#1aa3f5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
            >
              Ask
            </button>
          )}
        </div>
        {!canWrite && (
          <p className="text-sm text-gray-500 dark:text-gray-400">You're seeing what the client sees.</p>
        )}
      </header>

      {Array.isArray(parks) && parks.length > 1 && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <ParkFilter parks={parks} value={parkId} onChange={handleSelectPark} />
          {filteredParkName && (
            <button
              type="button"
              data-testid="park-filter-indicator"
              onClick={dismissFilter}
              className="self-start md:self-auto inline-flex items-center gap-1.5 rounded-full bg-[#32B1FF]/10 text-[#32B1FF] px-3 py-1.5 text-sm font-medium hover:bg-[#32B1FF]/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
            >
              Showing {filteredParkName}
              <span aria-hidden="true" className="text-[#32B1FF]/70">✕</span>
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <main className="lg:col-span-8 space-y-4">
          <PanelErrorBoundary label="verdict">
            <VerdictPanel verdict={verdict} />
          </PanelErrorBoundary>

          {waiting && (
            <PanelErrorBoundary label="waiting_on_you">
              <WaitingOnYouPanel
                facts={waiting.facts}
                parks={parks}
                onOpenItem={handleOpenItem}
              />
            </PanelErrorBoundary>
          )}

          {tracker && (
            <PanelErrorBoundary label="tracker">
              <TrackerPanel
                facts={tracker.facts}
                parks={parks}
                onOpenItem={handleOpenItem}
                onShowAll={handleShowAll}
                onAsk={handleAsk}
              />
            </PanelErrorBoundary>
          )}

          {(done || team) && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-7" ref={doneRef} data-testid="done-recently-region">
                <PanelErrorBoundary label="done_recently">
                  {done && <DoneRecentlyPanel facts={done.facts} onOpenItem={handleOpenItem} onShowAll={handleShowAllDone} />}
                </PanelErrorBoundary>
              </div>
              <div className="lg:col-span-5">
                <PanelErrorBoundary label="team">
                  {team && <TeamPanel facts={team.facts} onOpenMember={handleOpenMember} />}
                </PanelErrorBoundary>
              </div>
            </div>
          )}

          {history && (
            <PanelErrorBoundary label="history">
              <HistoryPanel
                facts={history.facts}
                parkId={parkId}
                multiPark={Array.isArray(parks) && parks.length > 1}
                onShowAll={handleShowAllHistory}
                onOpenItem={handleOpenItem}
              />
            </PanelErrorBoundary>
          )}

          {unknowns.map((p) => (
            <PanelErrorBoundary key={p.panel} label={p.panel}>
              <GenericCard panel={p} title={p.title || p.panel} sentence={p.sentence || 'This panel is not yet available.'} />
            </PanelErrorBoundary>
          ))}
                </main>

        <aside className="lg:col-span-4 hidden lg:block">
          <div className="lg:sticky lg:top-4 space-y-4">
            <PanelErrorBoundary label="conversation">
              {conversation && (
                <ConversationPanel
                  facts={conversation.facts}
                  parkId={parkId}
                  parks={parks}
                  canWrite={canWrite}
                  onOpenItem={handleOpenItem}
                  onSelectPark={handleSelectPark}
                  focusMessageId={focusMessageId}
                  onMessageFocused={() => setFocusMessageId(null)}
                />
              )}
            </PanelErrorBoundary>
          </div>
        </aside>
      </div>

      <DrillOver
        stack={drillStack}
        onBack={popDrill}
        onClose={closeDrill}
        onRowDrill={handleRowDrill}
        renderBody={renderDrillBody}
      />

      <MessageTeamButton
        unread={conversation?.facts?.unread ?? 0}
        onClick={() => setMobileSheetOpen(true)}
      />
      {mobileSheetOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end" data-testid="conversation-sheet">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileSheetOpen(false)} />
          <div className="relative w-full max-h-[85vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-t-2xl p-2">
            <div className="flex justify-end p-2">
              <button type="button" onClick={() => setMobileSheetOpen(false)} aria-label="Close conversation"
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-2">✕</button>
            </div>
            <ConversationPanel
              facts={conversation?.facts}
              parkId={parkId}
              parks={parks}
              canWrite={canWrite}
              onOpenItem={handleOpenItem}
              onSelectPark={(id) => { setMobileSheetOpen(false); handleSelectPark(id); }}
              focusMessageId={focusMessageId}
              onMessageFocused={() => setFocusMessageId(null)}
            />
          </div>
        </div>
      )}

      <AskSheet
        open={askOpen}
        onClose={() => setAskOpen(false)}
        parks={parks}
        parkId={parkId}
        onSubmit={handleAskSubmit}
      />
          </div>
        </main>
  );
};

export default PortalPage;

